import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ArrowRight, Sparkles, UserSearch, Briefcase, Cloud, Layers,
  Cpu, Building2, Landmark, HeartPulse, ShieldCheck, Radio, ShoppingBag,
  CheckCircle2, Star, Quote, Zap, Target, TrendingUp,
} from 'lucide-react';
import {
  HERO, CORE_SERVICES, ENGAGEMENT_MODELS, PROCESS, INDUSTRIES,
  CLIENT_LOGOS, TESTIMONIALS, IMAGES, TALENT_ROLES,
} from '../mock/data';
import Reveal from '../components/Reveal';
import SectionHeader from '../components/SectionHeader';
import HeroVisual from '../components/HeroVisual';

const IconMap = {
  UserSearch, Briefcase, Cloud, Layers, Cpu, Building2, Landmark,
  HeartPulse, ShieldCheck, Radio, ShoppingBag,
};

const Icon = ({ name, className = 'w-6 h-6' }) => {
  const C = IconMap[name] || Sparkles;
  return <C className={className} />;
};

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section className="relative min-h-[92vh] flex items-center overflow-hidden">
    <div className="absolute inset-0 grid-bg" />
    <div className="hero-glow" />
    {/* Floating orb accents */}
    <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-cyan-500/10 blur-[100px] floating" />
    <div className="absolute top-40 -right-32 w-96 h-96 rounded-full bg-violet-500/10 blur-[100px] floating" style={{ animationDelay: '2s' }} />

    <div className="container-x relative z-10 pt-16 pb-16">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6 fade-up">
            <Sparkles className="w-3.5 h-3.5" />
            {HERO.eyebrow}
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] text-white fade-up-delay-1">
            {HERO.title} <span className="shimmer-text">{HERO.titleAccent}</span>
          </h1>
          <p className="text-white/60 text-lg md:text-xl mt-6 max-w-xl leading-relaxed fade-up-delay-2">
            {HERO.subtitle}
          </p>
          <div className="flex flex-wrap gap-3 mt-8 fade-up-delay-3">
            <Link to={HERO.primaryCta.to} className="btn-primary inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm">
              {HERO.primaryCta.label} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to={HERO.secondaryCta.to} className="btn-outline inline-flex items-center gap-1.5 px-6 py-3.5 rounded-full text-sm">
              {HERO.secondaryCta.label} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Stats grid */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl fade-up-delay-3">
            {HERO.stats.map((s, i) => (
              <div key={i} className="border-l border-cyan-400/30 pl-4">
                <div className="font-display text-2xl md:text-3xl font-bold text-white">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5 relative fade-up-delay-2">
          <HeroVisual />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- CLIENT LOGOS ---------------- */
const ClientMarquee = () => (
  <section className="relative py-14 border-y border-white/5 overflow-hidden">
    <div className="container-x mb-8">
      <p className="text-center text-xs uppercase tracking-[0.3em] text-white/40">
        Trusted by teams at Fortune 1000 companies
      </p>
    </div>
    <div className="relative overflow-hidden">
      <div className="flex marquee-track gap-16 w-max">
        {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((name, i) => (
          <div key={i} className="font-display text-2xl md:text-3xl font-semibold text-white/30 hover:text-white transition-colors whitespace-nowrap">
            {name}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- SERVICES ---------------- */
const ServicesGrid = () => (
  <section className="section">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <SectionHeader
          eyebrow="What We Do"
          title="Your one-stop partner for"
          accent="IT, Talent & SaaS"
          subtitle="From critical engineering roles to full-stack technology delivery — we combine deep industry expertise with a rigorously vetted network."
        />
        <Reveal delay={200}>
          <Link to="/services" className="btn-outline inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-sm self-start md:self-end">
            View all services <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {CORE_SERVICES.map((s, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="tilt-card glass rounded-2xl p-7 h-full group">
              <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 mb-5 group-hover:bg-cyan-400/20 transition-colors">
                <Icon name={s.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-semibold text-white mb-2">{s.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              <div className="mt-6 inline-flex items-center gap-1 text-xs text-cyan-300 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- ABOUT SNIPPET ---------------- */
const AboutSnippet = () => (
  <section className="section relative overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-500/5 blur-[120px] rounded-full" />
    <div className="container-x relative">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div className="relative">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-white/10">
                <img src={IMAGES.aboutA} alt="Team collaboration" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 translate-y-8">
                <img src={IMAGES.aboutB} alt="Business meeting" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
          <div className="absolute -bottom-2 -left-2 glass rounded-xl px-5 py-4 floating">
            <div className="font-display text-3xl font-bold text-cyan-300">10+ yrs</div>
            <div className="text-xs text-white/50 uppercase tracking-wider mt-1">In IT recruitment</div>
          </div>
        </div>
        <div>
          <SectionHeader
            eyebrow="About Us"
            title="We bridge the gap between"
            accent="talent & opportunity."
            subtitle="The Tech Reach is an IT recruitment agency delivering fast, accurate matchmaking for the world's most ambitious companies. With deep expertise and a strong nationwide network, we help scale tech teams, fill leadership roles and navigate complex hiring — confidently."
          />
          <Reveal delay={200}>
            <ul className="mt-8 space-y-3">
              {[
                'Pre-vetted senior engineers across AI/ML, cloud, data & platform',
                'Contract, contract-to-hire and direct hire — all handled end-to-end',
                'Compliance, payroll and W-2 employment taken off your plate',
                'Onboarding support and ongoing consultant care built-in',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={300}>
            <Link to="/about" className="btn-outline inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-sm mt-8">
              About The Tech Reach <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- TALENT ROLES ---------------- */
const TalentRoles = () => (
  <section className="section">
    <div className="container-x">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <SectionHeader
          eyebrow="Talent Solutions"
          title="Access a pre-vetted network of"
          accent="AI, ML & Data engineers."
          subtitle="As AI initiatives scale from pilot to production, the demand for specialised engineering talent outpaces what traditional hiring channels can deliver. We bridge that gap."
        />
        <Reveal delay={200}>
          <div className="glass rounded-2xl p-8">
            <div className="flex items-center gap-2 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-5">
              <Target className="w-4 h-4" /> Roles we source
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {TALENT_ROLES.map((r, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm text-white/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 shrink-0" />
                  {r}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-7 pt-6 border-t border-white/5">
              <Link to="/talent" className="btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm">
                Explore talent solutions <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Engagement models */}
      <div className="grid md:grid-cols-3 gap-5 mt-14">
        {ENGAGEMENT_MODELS.map((m, i) => (
          <Reveal key={i} delay={i * 100}>
            <div className="tilt-card glass rounded-2xl p-7 h-full">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 text-[10px] uppercase tracking-widest font-medium mb-4">
                {m.tag}
              </div>
              <h3 className="font-display text-xl font-semibold text-white mb-2">{m.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed mb-5">{m.desc}</p>
              <ul className="space-y-2">
                {m.perks.map((p, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- PROCESS ---------------- */
const ProcessSection = () => (
  <section className="section relative overflow-hidden">
    <div className="absolute inset-0 grid-bg opacity-40" />
    <div className="container-x relative">
      <SectionHeader
        align="center"
        eyebrow="How it works"
        title="A precise, transparent"
        accent="4-step process."
        subtitle="We scope, source and match with precision — reducing time-to-placement without cutting corners on technical fit or cultural alignment."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {PROCESS.map((p, i) => (
          <Reveal key={i} delay={i * 120}>
            <div className="tilt-card glass rounded-2xl p-7 h-full relative">
              <div className="font-display text-6xl font-bold text-white/5 absolute top-4 right-5">{p.step}</div>
              <div className="w-10 h-10 rounded-lg bg-cyan-400 text-[#05070E] font-bold flex items-center justify-center mb-5">
                {p.step}
              </div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">{p.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- INDUSTRIES ---------------- */
const IndustriesSection = () => (
  <section className="section">
    <div className="container-x">
      <SectionHeader
        eyebrow="Industries we serve"
        title="Deep expertise across"
        accent="regulated & tech-forward sectors."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={i} delay={i * 70}>
            <div className="tilt-card glass rounded-2xl p-6 flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 flex items-center justify-center shrink-0">
                <Icon name={ind.icon} className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-display font-semibold text-white mb-1">{ind.name}</h4>
                <p className="text-sm text-white/60 leading-relaxed">{ind.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- TESTIMONIALS ---------------- */
const TestimonialsSection = () => (
  <section className="section relative overflow-hidden">
    <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-violet-500/5 blur-[120px] rounded-full" />
    <div className="container-x relative">
      <SectionHeader
        align="center"
        eyebrow="Testimonials"
        title="Loved by teams that"
        accent="choose to move fast."
      />
      <div className="grid md:grid-cols-3 gap-5 mt-12">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 120}>
            <div className="tilt-card glass rounded-2xl p-7 h-full flex flex-col">
              <Quote className="w-8 h-8 text-cyan-400/50 mb-4" />
              <p className="text-white/80 leading-relaxed flex-1">“{t.quote}”</p>
              <div className="flex items-center gap-1 mt-5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-cyan-400 text-cyan-400" />)}
              </div>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/5">
                <img src={t.avatar} alt={t.author} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                <div>
                  <div className="font-medium text-white text-sm">{t.author}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CTA ---------------- */
const CTA = () => (
  <section className="section">
    <div className="container-x">
      <div className="relative rounded-3xl overflow-hidden border border-white/10 p-10 md:p-16">
        <div className="absolute inset-0">
          <img src={IMAGES.netA} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#05070E] via-[#05070E]/85 to-[#05070E]/70" />
        </div>
        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-5">
              <TrendingUp className="w-3.5 h-3.5" /> Ready when you are
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05]">
              Ready to fill a role? <span className="shimmer-text">Let's talk.</span>
            </h2>
            <p className="text-white/70 text-lg mt-5 max-w-lg leading-relaxed">
              Talk to our team about your IT staffing needs. First qualified shortlist in 48 hours — no long-winded discovery calls.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
            <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-1.5 px-6 py-4 rounded-full text-sm">
              Book a free consultation <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/careers" className="btn-outline inline-flex items-center justify-center gap-1.5 px-6 py-4 rounded-full text-sm">
              Explore open roles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <ClientMarquee />
    <ServicesGrid />
    <AboutSnippet />
    <TalentRoles />
    <ProcessSection />
    <IndustriesSection />
    <TestimonialsSection />
    <CTA />
  </>
);

export default Home;
